import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link } from '@mui/icons-material';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { IconButton, MenuItem, Typography } from '@mui/material';
import BreadCrumbTooltip from 'shared/components/bread-crumb/bread-crumb-tooltip/bread-crumb-tooltip';
import SelectField from 'shared/components/formFields/select-field';

import { useSystemsContextProvider } from 'features/systems/context/systems-provider';
import { SystemStatus } from 'features/systems/domain/models/system-status';
import { useIS32ContextProvider } from 'features/systems/IS32/context/IS32-provider';
import { IS32LocalBusUnit } from 'features/systems/IS32/domain/models/IS32-localbus-unit';
import { IS32OPEventType } from 'features/systems/IS32/domain/models/IS32-OP-event-type';
import { useIS32DetailsContextProvider } from 'features/systems/IS32/providers/IS32-details.provider';
import styles from 'features/systems/IS32/views/configuration-components/localbus-units-components/IS32-localbus-unit-po-entry.module.scss';

interface Props {
  poEntryIndex: number;
  item: IS32LocalBusUnit;
}

const IS32LocalbusUnitPOEntry = ({ poEntryIndex, item }: Props) => {
  const { t } = useTranslation('IS32');
  const { t: tIOEventTypes } = useTranslation('ioEventTypes');

  const { control, getValues } = useFormContext();

  const {
    systemsDetailsHook: { viewingMode: systemViewingMode, selectedSystem },
  } = useSystemsContextProvider();

  const { openDeviceDetailsByExternalId } = useIS32ContextProvider();

  const {
    selectedIS32ConfigurationUnit,
    viewingMode,
    getLocalbusUnitViewingMode,
    localbusUnitPOEntryHasDevice,
    localbusUnitPOEntryHasLinkedDevice,
    getLocalbusUnitPOEntryDeviceBreadCrumbs,
    is32OverflowMenuHook: { onOverflowMenuClicked },
  } = useIS32DetailsContextProvider();

  const isSystemOffline = selectedSystem?.status === SystemStatus.offline;

  const fieldNameLow = `localBusUnits.${item.index}.events.${poEntryIndex}.eventTypeLow`;
  const fieldNameHigh = `localBusUnits.${item.index}.events.${poEntryIndex}.eventTypeHigh`;

  const fieldIdLow = `localbusUnit-${item.index}-poEntry-${poEntryIndex}-low`;
  const fieldIdHigh = `localbusUnit-${item.index}-poEntry-${poEntryIndex}-high`;

  const currentFormEventLow = getValues(fieldNameLow);
  const currentFormEventHigh = getValues(fieldNameHigh);

  const itemViewingMode = getLocalbusUnitViewingMode(item.index);

  return (
    <div className={styles.poEntryRowAsGridSubItems}>
      <div className={styles.titleContainer}>
        <div className={styles.titleText}>
          <Typography
            variant="body2"
            className={`${
              systemViewingMode === 'viewing' && viewingMode === 'viewing' && localbusUnitPOEntryHasDevice(item.reference, poEntryIndex)
                ? styles.clickable
                : ''
            }`}
            onClick={() =>
              systemViewingMode === 'viewing' &&
              viewingMode === 'viewing' &&
              localbusUnitPOEntryHasDevice(item.reference, poEntryIndex) &&
              openDeviceDetailsByExternalId(`${selectedIS32ConfigurationUnit?.address}_subunit_${item.reference}_${poEntryIndex}`)
            }
          >
            {t(`configuration.localbusUnitsConfiguration.entry${poEntryIndex}`)}:
          </Typography>
        </div>

        {localbusUnitPOEntryHasLinkedDevice(item.reference, poEntryIndex) && (
          <div className={styles.linkButton}>
            <BreadCrumbTooltip allElementNames={getLocalbusUnitPOEntryDeviceBreadCrumbs(item.reference, poEntryIndex)}>
              <Link sx={{ fontSize: 18 }} />
            </BreadCrumbTooltip>
          </div>
        )}
      </div>

      {viewingMode === 'editing' && itemViewingMode === 'editing' ? (
        <div className={styles.selectContainer}>
          <Controller
            name={fieldNameLow}
            control={control}
            render={({ field }) => {
              const controlledValue = field.value === undefined ? IS32OPEventType.NoEvent : field.value;
              return (
                <SelectField
                  {...field}
                  value={controlledValue}
                  onChange={(e) => field.onChange(e.target.value)}
                  id={fieldIdLow}
                  label={t('configuration.localbusUnitsConfiguration.low')}
                  variant="outlined"
                  className={styles.selectEvent}
                >
                  {Object.values(IS32OPEventType).map((option) => {
                    return (
                      <MenuItem key={`eventTypes-${option}-low-${poEntryIndex}`} value={option}>
                        {tIOEventTypes(`${option}`)}
                      </MenuItem>
                    );
                  })}
                </SelectField>
              );
            }}
          />
          <Controller
            name={fieldNameHigh}
            control={control}
            render={({ field }) => {
              const controlledValue = field.value === undefined ? IS32OPEventType.NoEvent : field.value;
              return (
                <SelectField
                  {...field}
                  value={controlledValue}
                  onChange={(e) => field.onChange(e.target.value)}
                  id={fieldIdHigh}
                  label={t('configuration.localbusUnitsConfiguration.high')}
                  variant="outlined"
                  className={styles.selectEvent}
                >
                  {Object.values(IS32OPEventType).map((option) => {
                    return (
                      <MenuItem key={`eventTypes-${option}-high-${poEntryIndex}`} value={option}>
                        {tIOEventTypes(`${option}`)}
                      </MenuItem>
                    );
                  })}
                </SelectField>
              );
            }}
          />
        </div>
      ) : (
        <div className={styles.highLowContainer}>
          <div className={styles.highLowLabels}>
            <Typography variant="subtitle1" sx={{ lineHeight: '16px' }}>
              {t('configuration.localbusUnitsConfiguration.low')}
              {':'}
            </Typography>
            <Typography variant="subtitle1" sx={{ lineHeight: '16px' }}>
              {t('configuration.localbusUnitsConfiguration.high')}
              {':'}
            </Typography>
          </div>
          <div className={styles.highLowValues}>
            <Typography
              variant="subtitle1"
              sx={{
                lineHeight: '16px',
                whiteSpace: 'nowrap',
              }}
            >
              {currentFormEventLow ? tIOEventTypes(`${currentFormEventLow}`) : tIOEventTypes('NoEvent')}
            </Typography>
            <Typography
              variant="subtitle1"
              sx={{
                lineHeight: '16px',
                whiteSpace: 'nowrap',
              }}
            >
              {currentFormEventHigh ? tIOEventTypes(`${currentFormEventHigh}`) : tIOEventTypes('NoEvent')}
            </Typography>
          </div>
        </div>
      )}

      <div>
        {systemViewingMode === 'viewing' &&
          viewingMode === 'viewing' &&
          itemViewingMode === 'viewing' &&
          !isSystemOffline &&
          !localbusUnitPOEntryHasDevice(item.reference, poEntryIndex) && (
            <IconButton onClick={(event) => onOverflowMenuClicked(event, item, poEntryIndex)} className={styles.moreButton}>
              <MoreHorizIcon className={styles.moreIcon} />
            </IconButton>
          )}
      </div>
    </div>
  );
};

export default IS32LocalbusUnitPOEntry;
